import { useState } from "react";
import { useCheckinGate } from "@/hooks/useCheckinGate";
import { DoorOpen, CheckCircle, Eye, PlusCircle, LogOut, Sparkles, ArrowLeftRight, CalendarCheck, XCircle, Wrench, AlertTriangle, Lock, LockOpen, Info } from "lucide-react";
import { ActionButton, SectionLabel } from "./ActionButton";
import { fmtINR } from "@/lib/documents";

const fmtD = (s) => { if (!s) return "—"; const d = new Date(s.length === 10 ? `${s}T00:00:00` : s); return isNaN(d) ? s.slice(0, 10) : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }); };
const fmtDY = (s) => { if (!s) return "—"; const d = new Date(s.length === 10 ? `${s}T00:00:00` : s); return isNaN(d) ? s.slice(0, 10) : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); };
const fmtDT = (s) => { if (!s) return "—"; const d = new Date(s); return isNaN(d) ? s : `${d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}, ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`; };

function Strip({ tone = "info", title, sub, testid }) {
  const bg = tone === "warning" ? "bg-[#FEF3C7]" : "bg-[#E6FEFF]";
  return (
    <div className={`${bg} px-5 py-3 -mx-5`} data-testid={testid}>
      <div className="text-[13px] font-bold text-[#1A2425]">{title}</div>
      {sub && <div className="text-[12px] text-[#3F5257] mt-0.5">{sub}</div>}
    </div>
  );
}

/** Per-status quick actions (spec: Feature 1). `run(key)` is provided by the drawer. */
export function QuickActions({ ctx, run, busy }) {
  const { state, stay, reservation, work_order: wo, room } = ctx;
  const has = (k) => ctx.available_actions.includes(k);
  const gate = useCheckinGate();
  const resGated = !!reservation && gate.isBlocked(reservation.arrival_date);

  if (state === "occupied" && stay) {
    return (
      <div className="space-y-5">
        <Strip title={stay.guest_name || "Current guest"} testid="drawer-guest-strip"
          sub={`Checked in: ${fmtD(stay.actual_arrival)} · Due out: ${fmtD(stay.expected_departure)} · ${stay.nights || 0} night${stay.nights === 1 ? "" : "s"}${stay.folio_balance != null ? ` · ${stay.folio_balance < 0 ? `Credit ${fmtINR(-stay.folio_balance)}` : `Balance ${fmtINR(stay.folio_balance)}`}` : ""}`} />
        <div>
          <SectionLabel>Stay Actions</SectionLabel>
          <div className="space-y-2">
            <ActionButton icon={Eye} title="View Current Stay" subtitle="Open full stay details" onClick={() => run("view_stay")} testid="qa-view-stay" />
            <ActionButton icon={PlusCircle} title="Add Charge" subtitle="Post F&B, laundry or misc charge" onClick={() => run("add_charge")} testid="qa-add-charge" />
            <ActionButton icon={LogOut} title="Check Out Guest" subtitle="Generate bill & checkout" tone="primary" onClick={() => run("check_out")} testid="qa-check-out" disabled={busy} />
          </div>
        </div>
        <div>
          <SectionLabel>Room Actions</SectionLabel>
          <div className="space-y-2">
            <ActionButton icon={Sparkles} title="Request Housekeeping" tone="info" compact onClick={() => run("request_housekeeping")} testid="qa-request-hk" disabled={busy} />
            <ActionButton icon={ArrowLeftRight} title="Move Guest to Another Room" compact onClick={() => run("move_guest")} testid="qa-move-guest" />
          </div>
        </div>
      </div>
    );
  }

  if (state === "reserved" && reservation) {
    return (
      <div className="space-y-5">
        <Strip title={reservation.guest_name || "Reserved"} testid="drawer-reservation-strip"
          sub={`Arriving: ${fmtDY(reservation.arrival_date)} · ${reservation.nights || 0} night${reservation.nights === 1 ? "" : "s"}${reservation.advance_paid_total ? ` · Advance ${fmtINR(reservation.advance_paid_total)}` : ""} · ${reservation.code}`} />
        <div>
          <SectionLabel>Guest Actions</SectionLabel>
          <div className="space-y-2">
            <ActionButton icon={CalendarCheck} title="View Reservation" subtitle="Open booking details" onClick={() => run("view_reservation")} testid="qa-view-reservation" />
            <ActionButton icon={CheckCircle} title="Check-In Guest" subtitle={resGated ? gate.reason(reservation.arrival_date) : "Guest is here now"} tone="primary" onClick={() => run("check_in")} testid="qa-check-in" disabled={resGated} />
            <ActionButton icon={XCircle} title="Cancel Reservation" tone="error" compact onClick={() => run("cancel_reservation")} testid="qa-cancel-reservation" />
          </div>
        </div>
      </div>
    );
  }

  if (state === "out_of_order") {
    return (
      <div className="space-y-5">
        <Strip tone="warning" title={wo?.title || room.maintenance_reason || room.block_reason || "Room out of order"} testid="drawer-issue-strip"
          sub={wo ? `${wo.code} · Since: ${fmtDY(wo.reported_at)} · ${(wo.state || "").replace(/_/g, " ")}` : "No open work order linked"} />
        <div>
          <SectionLabel>Maintenance</SectionLabel>
          <div className="space-y-2">
            <ActionButton icon={Wrench} title="View Maintenance Log" subtitle="Engineering work orders for this room" onClick={() => run("view_maintenance")} testid="qa-view-maintenance" />
            <ActionButton icon={CheckCircle} title="Mark as Available" subtitle="Back in service · ready to sell" tone="primary" onClick={() => run("mark_available")} testid="qa-mark-available" disabled={busy} />
            <ActionButton icon={PlusCircle} title="Log New Issue" subtitle="Raise a work order" onClick={() => run("log_issue")} testid="qa-log-issue" />
          </div>
        </div>
      </div>
    );
  }

  if (state === "blocked") {
    return (
      <div className="space-y-5">
        <div>
          <SectionLabel>Guest Actions</SectionLabel>
          <div className="space-y-2">
            <LockedButton icon={DoorOpen} title="New Walk-In" subtitle="Check in a guest right now" />
            <LockedButton icon={CheckCircle} title="Check-In Guest" subtitle="Guest is here now" />
          </div>
          <div className="mt-2 text-center text-[11px] italic text-[#97A6A9]" data-testid="blocked-actions-note">Actions unavailable while room is blocked</div>
        </div>
        <RoomManagement ctx={ctx} run={run} busy={busy} dirty={room.housekeeping_status === "dirty"} />
      </div>
    );
  }

  // vacant_clean / vacant_dirty
  const dirty = state === "vacant_dirty";
  const hasRes = !!reservation;
  return (
    <div className="space-y-5">
      <div>
        <SectionLabel>Guest Actions</SectionLabel>
        <div className="space-y-2">
          <ActionButton icon={DoorOpen} title="New Walk-In" subtitle="Check in a guest right now" onClick={() => run("walk_in")} testid="qa-walk-in" />
          {has("check_in") && (
            <ActionButton icon={CheckCircle} title="Check-In Guest" tone="primary" onClick={() => run("check_in")} testid="qa-check-in" disabled={resGated}
              subtitle={resGated ? `${reservation.guest_name} · ${gate.reason(reservation.arrival_date)}` : hasRes ? `${reservation.guest_name} · arriving ${fmtD(reservation.arrival_date)}` : "No booking assigned — opens Walk-In"} />
          )}
        </div>
        {dirty && <div className="mt-2 flex items-center gap-1.5 text-[12px] text-[#78530E]"><AlertTriangle size={13} strokeWidth={1.5} />Room needs cleaning before a guest can be checked in.</div>}
      </div>
      <RoomManagement ctx={ctx} run={run} busy={busy} dirty={dirty} />
    </div>
  );
}

function LockedButton({ icon: Icon, title, subtitle }) {
  return (
    <div className="relative w-full h-14 rounded-lg px-4 flex items-center gap-3 bg-[#F4FCFC] border border-[#E6EFF0] text-[#D5E6E8] cursor-not-allowed select-none" aria-disabled data-testid={`qa-locked-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon size={22} strokeWidth={1.5} color="#D5E6E8" />
      <span><span className="block font-bold text-[14px] leading-tight">{title}</span><span className="block text-[12px] leading-tight">{subtitle}</span></span>
      <Lock size={12} strokeWidth={1.5} color="#D5E6E8" className="absolute top-2 right-2" />
    </div>
  );
}

function MgmtButton({ icon: Icon, title, tone, onClick, disabled, testid, note }) {
  const cls = disabled ? "bg-[#F4FCFC] border-[#E6EFF0] text-[#D5E6E8] cursor-not-allowed"
    : tone === "warning" ? "bg-[#FEF3C7] border-[#F59E0B] text-[#78530E]" : tone === "error" ? "bg-[#FEE2E2] border-[#DC2626] text-[#991B1B]"
    : tone === "success" ? "bg-[#DBFCEF] border-[#14C48E] text-[#014751]" : tone === "mint" ? "bg-[#DBFCEF] border-[#14C48E] text-[#014751]" : "bg-[#EEF1F4] border-[#B7CACD] text-[#3A4A5A]";
  return (
    <div className="min-w-0">
      <button type="button" onClick={onClick} disabled={disabled} data-testid={testid} aria-disabled={disabled}
        className={`w-full h-11 rounded-lg border px-2.5 flex items-center gap-1.5 text-[12.5px] font-bold transition-[filter] hover:brightness-[0.98] ${cls}`}>
        <Icon size={18} strokeWidth={1.5} className="shrink-0" /><span className="truncate">{title}</span>
      </button>
      {disabled && note && <div className="text-[11px] text-[#97A6A9] mt-1 px-1">{note}</div>}
    </div>
  );
}

/** Room Management 2×2 grid + block strip + inline unblock confirmation (ROOM-BLOCK-1). */
function RoomManagement({ ctx, run, busy, dirty }) {
  const [confirm, setConfirm] = useState(false);
  const { room, block, scheduled_block: sched } = ctx;
  const isBlocked = ctx.state === "blocked" && !!block;
  const target = isBlocked ? block : sched;
  const canUnblock = !!target;
  return (
    <div>
      <SectionLabel>Room Management</SectionLabel>
      {isBlocked && (
        <div className="mb-2 rounded-r-[6px] border-l-[3px] border-[#B7CACD] bg-[#EEF1F4] px-3.5 py-2.5" data-testid="block-info-strip">
          <div className="flex items-center gap-1.5 text-[13px] font-bold text-[#3A4A5A]"><Lock size={14} strokeWidth={1.5} />Blocked: {fmtD(block.block_from)} → {fmtDY(block.block_to)} ({block.nights} night{block.nights === 1 ? "" : "s"})</div>
          <div className="text-[12px] text-[#3F5257] mt-0.5">Reason: {block.reason_label}{block.reason_note ? ` · ${block.reason_note}` : ""}</div>
          <div className="text-[11px] text-[#97A6A9] mt-0.5">Blocked by: {block.blocked_by_name || "—"} · {fmtDT(block.blocked_at)}</div>
        </div>
      )}
      {!isBlocked && sched && (
        <div className="mb-2 rounded-r-[6px] border-l-[3px] border-[#014751] bg-[#E6FEFF] px-3.5 py-2.5 text-[12px] text-[#014751] flex items-center gap-1.5" data-testid="scheduled-block-strip">
          <Info size={14} strokeWidth={1.5} />Scheduled block: {fmtD(sched.block_from)} → {fmtDY(sched.block_to)} · {sched.reason_label}
        </div>
      )}
      {confirm && target ? (
        <div className="rounded-lg border border-[#14C48E] bg-[#DBFCEF] px-4 py-3.5" data-testid="unblock-confirm">
          <div className="flex items-center gap-1.5 text-[13px] font-bold text-[#014751]"><LockOpen size={16} strokeWidth={1.5} />{isBlocked ? `Unblock Room ${room.room_number}?` : `Cancel scheduled block for Room ${room.room_number}?`}</div>
          <div className="text-[12px] text-[#3F5257] mt-1">{isBlocked ? `Currently blocked until ${fmtDY(target.block_to)} · ${target.reason_label}` : `Scheduled ${fmtD(target.block_from)} → ${fmtDY(target.block_to)} · ${target.reason_label}`}</div>
          <div className="text-[12px] text-[#3F5257] mt-1">{isBlocked ? "Unblocking now will make this room available immediately." : "This block hasn't started yet. Cancelling removes the scheduled block."}</div>
          <div className="flex justify-end gap-2 mt-2.5">
            <button type="button" onClick={() => setConfirm(false)} className="h-9 px-3 text-[12.5px] text-[#3F5257]" data-testid="unblock-cancel">Cancel</button>
            <button type="button" disabled={busy} onClick={async () => { await run("unblock", target.id); setConfirm(false); }} className="h-9 px-3 rounded-[6px] bg-[#37F4B5] text-[#014751] text-[12.5px] font-bold hover:bg-[#2CE3A6] disabled:opacity-50" data-testid="unblock-confirm-yes">{isBlocked ? "Yes, Unblock Now" : "Yes, Cancel Block"}</button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {dirty && !isBlocked
            ? <MgmtButton icon={CheckCircle} title="Mark as Clean" tone="success" onClick={() => run("mark_clean")} disabled={busy} testid="qa-mark-clean" />
            : <MgmtButton icon={AlertTriangle} title="Mark as Dirty" tone="warning" onClick={() => run("mark_dirty")} disabled={busy || isBlocked} testid="qa-mark-dirty" />}
          <MgmtButton icon={XCircle} title="Mark Out of Order" tone="error" onClick={() => run("mark_out_of_order")} disabled={busy} testid="qa-mark-ooo" />
          <MgmtButton icon={Lock} title="Block Room" onClick={() => run("block")} disabled={busy || isBlocked} testid="qa-block-room" note={isBlocked ? "Already blocked" : null} />
          <MgmtButton icon={LockOpen} title="Unblock Room" tone="mint" onClick={() => setConfirm(true)} disabled={busy || !canUnblock} testid="qa-unblock-room" note={!canUnblock ? "Not blocked" : null} />
        </div>
      )}
    </div>
  );
}
